import type { DiffereCompetitionType, DiffereNavigationRequest } from '../types/app';

const DIFFERE_TYPES: DiffereCompetitionType[] = ['indiv', 'tripl', 'quad'];

function parseDiffereType(value: string | null): DiffereCompetitionType | undefined {
  if (value && DIFFERE_TYPES.includes(value as DiffereCompetitionType)) {
    return value as DiffereCompetitionType;
  }
  return undefined;
}

function parsePositiveInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Lit les paramètres différé depuis une query string ou l'URL courante. */
export function parseDiffereDeepLink(search?: string): DiffereNavigationRequest | null {
  const raw = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);

  const diffType = parseDiffereType(params.get('differeType'));
  const differeId = parsePositiveInt(params.get('differeId'));
  if (!diffType || differeId == null) return null;

  const playerPseudo = params.get('differePlayer')?.trim() || undefined;
  const dealNum = parsePositiveInt(params.get('differeDeal'));
  const partieId = parsePositiveInt(params.get('differePartie'));
  const viewRaw = params.get('differeView');
  const view = viewRaw === 'donnes' ? 'donnes' : 'replay';

  if (view === 'replay' && dealNum == null && partieId == null) return null;
  if (view === 'donnes' && !playerPseudo) return null;

  return {
    diffType,
    differeId,
    playerPseudo,
    dealNum,
    partieId,
    view,
  };
}

/** Construit un lien partageable vers une donne différé. */
export function buildDiffereShareUrl(request: DiffereNavigationRequest): string {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set('differeType', request.diffType);
  url.searchParams.set('differeId', String(request.differeId));

  if (request.playerPseudo) {
    url.searchParams.set('differePlayer', request.playerPseudo);
  }
  if (request.dealNum != null) {
    url.searchParams.set('differeDeal', String(request.dealNum));
  }
  if (request.partieId != null) {
    url.searchParams.set('differePartie', String(request.partieId));
  }
  if (request.view && request.view !== 'replay') {
    url.searchParams.set('differeView', request.view);
  }

  return url.toString();
}

/** Retire les paramètres différé de l'URL. */
export function clearDiffereUrlParams(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  url.searchParams.delete('differeType');
  url.searchParams.delete('differeId');
  url.searchParams.delete('differePlayer');
  url.searchParams.delete('differeDeal');
  url.searchParams.delete('differePartie');
  url.searchParams.delete('differeView');

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}
